"use client";

import { useState } from "react";
import { loginAction } from "@/lib/actions/auth.actions";
import { Building2, HardHat, BarChart3, FileText } from "lucide-react";

export default function LoginPage() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(formData: FormData) {
    setLoading(true);
    setError(null);
    const result = await loginAction(formData);
    if (result?.error) {
      setError(result.error);
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branded */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B2A4A] flex-col justify-between p-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/3" />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/3" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#2E86C1]/10" />
        </div>

        {/* Logo */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2E86C1] rounded-xl flex items-center justify-center shadow-lg">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-bold text-lg text-white tracking-tight">Chantiers BTP</span>
              <p className="text-[10px] text-white/40 uppercase tracking-widest leading-none">Pro</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="relative space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-white leading-tight">
              Gérez vos chantiers<br />en toute simplicité
            </h2>
            <p className="text-white/50 mt-3 text-sm leading-relaxed">
              Suivi complet de vos projets BTP — de la prospection<br />à la facturation, en passant par le planning terrain.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: HardHat, text: "Suivi chantiers & tâches en temps réel" },
              { icon: FileText, text: "Devis et facturation intégrés" },
              { icon: BarChart3, text: "Analytics et tableaux de bord" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-white/70" />
                </div>
                <p className="text-sm text-white/70">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative">
          <p className="text-xs text-white/30">© 2025 Chantiers BTP Pro — Solution de gestion</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F4F6F9]">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 bg-[#1B4F72] rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-gray-900">Chantiers BTP</span>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="mb-7">
              <h1 className="text-xl font-bold text-gray-900">Connexion</h1>
              <p className="text-gray-500 text-sm mt-1">Accédez à votre espace de gestion</p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                <span className="text-red-500 mt-0.5">⚠</span>
                {error}
              </div>
            )}

            {/* Form */}
            <form action={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Adresse email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="vous@entreprise.fr"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1B4F72]/20 focus:border-[#1B4F72]/60 outline-none transition-all bg-gray-50/50 placeholder:text-gray-400"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mot de passe
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1B4F72]/20 focus:border-[#1B4F72]/60 outline-none transition-all bg-gray-50/50 placeholder:text-gray-400"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 px-4 bg-[#1B4F72] text-white font-medium rounded-lg hover:bg-[#154360] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm mt-2"
              >
                {loading ? "Connexion en cours..." : "Se connecter"}
              </button>
            </form>

            {/* Demo accounts */}
            <div className="mt-6 pt-5 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-400 mb-2.5">Comptes de démonstration</p>
              <div className="space-y-1.5">
                {[
                  { role: "Admin", email: "admin@chantier-app.fr", pwd: "admin123" },
                  { role: "Commercial", email: "commercial@chantier-app.fr", pwd: "commercial123" },
                  { role: "Conducteur", email: "conducteur@chantier-app.fr", pwd: "conducteur123" },
                ].map(({ role, email, pwd }) => (
                  <div key={role} className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-semibold text-gray-700 w-20">{role}</span>
                    <span className="text-gray-400 font-mono">{email}</span>
                    <span className="text-gray-300">/</span>
                    <span className="font-mono">{pwd}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
